import { ReactNode } from 'react';
import { Alert, Box, Grid, Paper, Skeleton, Typography } from '@mui/material';
import { useGetInvestmentFinancialMetrics } from '@/lib/api/investment-financials';
import { formatCurrency } from '@/utils/misc';
import { GlossaryIcon } from '../shared/GlossaryHelp';
import { NOI_LABEL } from '../shared/metric-glossary';
import { cardSurface } from '../shared/surfaces';
import { MetricValue } from './MetricValue';
import { formatRatio, unavailableHint, unclassifiedHint } from './metrics-copy';

interface RealPerformanceBandProps {
  investmentId: string;
}

const Cell = ({
  label,
  help,
  children,
  caption,
}: {
  label: string;
  help?: ReactNode;
  children: ReactNode;
  caption?: string;
}) => (
  <Box>
    <Typography
      variant="overline"
      color="text.secondary"
      display="block"
      lineHeight={1.4}
      sx={{ minHeight: { sm: 34 } }}
    >
      {label}
      {help}
    </Typography>
    {children}
    {caption ? (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
        {caption}
      </Typography>
    ) : null}
  </Box>
);

export const RealPerformanceBand = ({ investmentId }: RealPerformanceBandProps) => {
  const year = new Date().getUTCFullYear();
  const {
    data: metrics,
    isLoading,
    isError,
  } = useGetInvestmentFinancialMetrics(investmentId, year);

  if (isError) {
    return (
      <Alert severity="error">
        No se pudo cargar la rentabilidad real de esta inversión. Vuelve a intentarlo más tarde.
      </Alert>
    );
  }

  if (isLoading) return <Skeleton variant="rounded" height={150} />;
  if (!metrics) return null;

  const unclassified = metrics.quality.unclassifiedLineCount;
  const attentionHint = unclassified > 0 ? unclassifiedHint(unclassified) : undefined;
  const marketReturn =
    metrics.actualAnnualized?.returnOnMarketValue ??
    metrics.annualEstimate?.returnOnMarketValue ??
    metrics.metrics.returnOnMarketValue;

  const marketReturnLabel = metrics.actualAnnualized
    ? 'Rentabilidad sobre valor (ritmo YTD)'
    : metrics.annualEstimate
      ? 'Rentabilidad sobre valor (previsión)'
      : 'Rentabilidad sobre valor';

  const marketReturnCaption =
    metrics.actualAnnualized || metrics.annualEstimate
      ? `${metrics.actualAnnualized ? 'Anualizada' : 'Previsión ficha'} · acumulada ${formatRatio(
          metrics.metrics.returnOnMarketValue
        )}${
          metrics.actualAnnualized && metrics.annualEstimate
            ? ` · previsión ficha ${formatRatio(metrics.annualEstimate.returnOnMarketValue)}`
            : ''
        }`
      : undefined;

  return (
    <Paper elevation={0} sx={{ p: 3, ...cardSurface }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Cell
            label={NOI_LABEL}
            help={
              <GlossaryIcon
                term="noi"
                extra={`Acumulado con los movimientos registrados en ${year}.`}
              />
            }
            caption="Acumulado este año"
          >
            <MetricValue
              metric={metrics.metrics.cashNoi}
              format={formatCurrency}
              variant="h5"
              color={(metrics.metrics.cashNoi.value ?? 0) >= 0 ? 'success.main' : 'error.main'}
              attentionHint={attentionHint}
            />
          </Cell>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Cell label={marketReturnLabel} caption={marketReturnCaption}>
            <MetricValue
              metric={marketReturn}
              format={() => formatRatio(marketReturn)}
              variant="h5"
              color="primary.main"
              unavailableHint={unavailableHint('returnOnMarketValue', metrics)}
              attentionHint={attentionHint}
            />
          </Cell>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Cell
            label="Cashflow tras deuda"
            help={<GlossaryIcon term="cashflow" />}
            caption="Acumulado este año"
          >
            <MetricValue
              metric={metrics.metrics.cashflowAfterDebt}
              format={formatCurrency}
              variant="h5"
              color={
                (metrics.metrics.cashflowAfterDebt.value ?? 0) >= 0 ? 'success.main' : 'error.main'
              }
              attentionHint={attentionHint}
            />
          </Cell>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Cell
            label="Valor de mercado"
            caption={metrics.valuation ? 'Última valoración registrada' : 'Sin valoración'}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              color={metrics.valuation ? undefined : 'text.disabled'}
            >
              {metrics.valuation ? formatCurrency(metrics.valuation.amount) : '—'}
            </Typography>
          </Cell>
        </Grid>
      </Grid>
    </Paper>
  );
};
